import React from "react";
import { useSelectionStore } from "../../../stores/selectionStore";
import { focusNewEntityInInspector } from "../../../utils/focusNewEntityInInspector";
import {
  addDefaultBody,
  addDefaultCollisionGeom,
  addDefaultInertia,
  addDefaultJoint,
  addDefaultSite,
  addDefaultVisualGeom,
} from "../../../utils/bodyAddActions";
import {
  addDefaultBodyConstraint,
  addDefaultJointConstraint,
  addDefaultTendon,
} from "../../../utils/constraintAddActions";
import { nextAvailableCameraName } from "../../../utils/nextAvailableCameraName";
import { dispatchViewportCameraVisibility } from "./sceneInventoryViewportEvents";

export function useSceneInventoryQuickAdd({
  bodies,
  cameras,
  equalities,
  joints,
  sceneActions,
  sceneOrder,
  selectedTargets,
  tendons,
  isPhysicsRunning,
}) {
  const firstBodyUuid = React.useMemo(
    () => sceneOrder.orderedBodyUuids[0] || Object.values(bodies || {})[0]?.uuid || null,
    [bodies, sceneOrder]
  );

  const firstBodyWithoutInertiaUuid = React.useMemo(
    () =>
      sceneOrder.orderedBodyUuids.find((bodyUuid) => {
        const body = bodies?.[bodyUuid];
        return body?.uuid && !body.inertia;
      }) || null,
    [bodies, sceneOrder]
  );

  const scalarJointUuids = React.useMemo(
    () =>
      Object.values(joints || {})
        .filter((joint) => joint?.uuid && (joint.kind === "hinge" || joint.kind === "slide"))
        .map((joint) => joint.uuid),
    [joints]
  );

  const preferredBodyUuid = React.useMemo(() => {
    if (selectedTargets.body && bodies?.[selectedTargets.body]) return selectedTargets.body;
    const selectedGeomBody = selectedTargets.geom?.split(":")?.[0];
    if (selectedGeomBody && bodies?.[selectedGeomBody]) return selectedGeomBody;
    const selectedInertialBody = selectedTargets.inertial?.split(":")?.[0];
    if (selectedInertialBody && bodies?.[selectedInertialBody]) return selectedInertialBody;
    if (selectedTargets.joint) {
      const owner = Object.values(bodies || {}).find((body) =>
        (body?.joints || []).includes(selectedTargets.joint)
      );
      if (owner?.uuid) return owner.uuid;
    }
    if (selectedTargets.site) {
      const owner = Object.values(bodies || {}).find((body) =>
        (body?.sites || []).includes(selectedTargets.site)
      );
      if (owner?.uuid) return owner.uuid;
    }
    if (selectedTargets.mesh) {
      const owner = Object.values(bodies || {}).find((body) =>
        (body?.geoms || []).some((geom) => String(geom?.mesh || "") === selectedTargets.mesh)
      );
      if (owner?.uuid) return owner.uuid;
    }
    return firstBodyUuid;
  }, [
    bodies,
    firstBodyUuid,
    selectedTargets.body,
    selectedTargets.geom,
    selectedTargets.inertial,
    selectedTargets.joint,
    selectedTargets.mesh,
    selectedTargets.site,
  ]);

  const selectedActuatorJointUuid =
    selectedTargets.joint && joints?.[selectedTargets.joint] ? selectedTargets.joint : null;
  const selectedActuatorTendonUuid =
    selectedTargets.tendon && tendons?.[selectedTargets.tendon] ? selectedTargets.tendon : null;
  const preferredJointUuid = React.useMemo(
    () =>
      selectedActuatorJointUuid ||
      scalarJointUuids[0] ||
      Object.values(joints || {})[0]?.uuid ||
      null,
    [joints, scalarJointUuids, selectedActuatorJointUuid]
  );

  const focusNewInventoryEntity = React.useCallback((applySelection, source = "sceneTree") => {
    focusNewEntityInInspector(source, applySelection);
  }, []);

  const ensurePreferredBody = React.useCallback(
    (allowCreate = true) => {
      if (preferredBodyUuid) return preferredBodyUuid;
      return allowCreate ? addDefaultBody(sceneActions, null) : null;
    },
    [preferredBodyUuid, sceneActions]
  );

  const getQuickAddDisabledReason = React.useCallback(
    (rowKey) => {
      if (isPhysicsRunning) return "Cannot add while physics is running";
      if (rowKey === "actuator" && !selectedActuatorJointUuid && !selectedActuatorTendonUuid) {
        return "Select a joint or tendon to create actuator";
      }
      if (rowKey === "inertial" && !firstBodyWithoutInertiaUuid) {
        return firstBodyUuid ? "All bodies already have inertials" : "Add a body first";
      }
      if (
        rowKey === "constraint" &&
        Object.keys(bodies || {}).length < 2 &&
        scalarJointUuids.length < 2
      ) {
        return "Add at least two bodies or two scalar joints first";
      }
      if (rowKey === "tendon" && scalarJointUuids.length === 0)
        return "Add a hinge or slide joint first";
      return "";
    },
    [
      bodies,
      firstBodyUuid,
      firstBodyWithoutInertiaUuid,
      isPhysicsRunning,
      scalarJointUuids,
      selectedActuatorJointUuid,
      selectedActuatorTendonUuid,
    ]
  );

  const handleQuickAdd = React.useCallback(
    (rowKey) => {
      if (getQuickAddDisabledReason(rowKey)) return;
      const selection = useSelectionStore.getState();

      if (rowKey === "object") {
        const bodyUuid = addDefaultBody(sceneActions, null);
        if (bodyUuid) {
          focusNewInventoryEntity(() => selection.selectBody(bodyUuid), "sceneTreeObject");
        }
        return;
      }

      if (rowKey === "body") {
        const bodyUuid = addDefaultBody(sceneActions, preferredBodyUuid || null);
        if (bodyUuid) {
          focusNewInventoryEntity(() => selection.selectBody(bodyUuid));
        }
        return;
      }

      if (rowKey === "joint") {
        const parentBodyUuid = ensurePreferredBody(true);
        const jointUuid = addDefaultJoint(sceneActions, parentBodyUuid, "hinge");
        if (jointUuid) {
          focusNewInventoryEntity(() => selection.selectJoint(jointUuid));
        }
        return;
      }

      if (rowKey === "site") {
        const parentBodyUuid = ensurePreferredBody(true);
        const siteUuid = addDefaultSite(sceneActions, parentBodyUuid ?? null);
        if (siteUuid) {
          focusNewInventoryEntity(() => selection.selectSite(siteUuid));
        }
        return;
      }

      if (rowKey === "collision") {
        const parentBodyUuid = ensurePreferredBody(true);
        const geomUuid = addDefaultCollisionGeom(sceneActions, parentBodyUuid, "box");
        if (geomUuid) {
          focusNewInventoryEntity(
            () => selection.selectGeom(`${parentBodyUuid}:${geomUuid}`),
            "scene"
          );
        }
        return;
      }

      if (rowKey === "visual") {
        const parentBodyUuid = ensurePreferredBody(true);
        const geomUuid = addDefaultVisualGeom(sceneActions, parentBodyUuid, "box");
        if (geomUuid) {
          focusNewInventoryEntity(
            () => selection.selectGeom(`${parentBodyUuid}:${geomUuid}`),
            "scene"
          );
        }
        return;
      }

      if (rowKey === "mesh") {
        const parentBodyUuid = ensurePreferredBody(true);
        if (!parentBodyUuid) return;
        window.dispatchEvent(
          new CustomEvent("start-visual-mesh-creation", {
            detail: { parentBodyUuid },
          })
        );
        return;
      }

      if (rowKey === "inertial") {
        const bodyUuid =
          preferredBodyUuid && !bodies?.[preferredBodyUuid]?.inertia
            ? preferredBodyUuid
            : firstBodyWithoutInertiaUuid;
        const inertiaUuid = bodyUuid ? addDefaultInertia(sceneActions, bodyUuid) : null;
        if (bodyUuid && inertiaUuid) {
          focusNewInventoryEntity(() => selection.selectInertial(`${bodyUuid}:${inertiaUuid}`));
        }
        return;
      }

      if (rowKey === "actuator") {
        const jointUuid = selectedActuatorJointUuid;
        const tendonUuid = selectedActuatorTendonUuid;
        const jointData = jointUuid ? joints?.[jointUuid] : null;
        const tendonData = tendonUuid ? tendons?.[tendonUuid] : null;
        const defaultCtrlrange = jointData
          ? Array.isArray(jointData.range) && jointData.range.length === 2
            ? [jointData.range[0], jointData.range[1]]
            : [-1, 1]
          : [-1, 1];
        const actuatorUuid = jointUuid
          ? sceneActions.addActuator({
              name: `${jointData?.name ?? "joint"}_actuator`,
              kind: "motor",
              joint: jointUuid,
              ctrlrange: defaultCtrlrange,
              gear: 1,
            })
          : sceneActions.addActuator({
              name: `${tendonData?.name ?? "tendon"}_actuator`,
              kind: "motor",
              tendonUuid,
              tendon: tendonData?.name ?? tendonUuid,
              ctrlrange: defaultCtrlrange,
              gear: 1,
            });
        if (actuatorUuid) {
          focusNewInventoryEntity(() =>
            selection.selectActuatorInParent(actuatorUuid, jointUuid ?? null, tendonUuid ?? null)
          );
        }
        return;
      }

      if (rowKey === "constraint") {
        const equalityUuid =
          selectedTargets.joint && scalarJointUuids.includes(selectedTargets.joint)
            ? addDefaultJointConstraint(
                sceneActions,
                joints || {},
                selectedTargets.joint,
                equalities || {}
              )
            : addDefaultBodyConstraint(
                sceneActions,
                bodies || {},
                preferredBodyUuid,
                "connect",
                equalities || {}
              );
        if (equalityUuid) {
          focusNewInventoryEntity(() => selection.selectEquality(equalityUuid));
        }
        return;
      }

      if (rowKey === "tendon") {
        const tendonUuid = addDefaultTendon(
          sceneActions,
          joints || {},
          preferredJointUuid,
          tendons || {}
        );
        if (tendonUuid) {
          focusNewInventoryEntity(() => selection.selectTendon(tendonUuid));
        }
        return;
      }

      if (rowKey === "camera") {
        const bodyUuid = preferredBodyUuid;
        const cameraUuid = bodyUuid
          ? sceneActions.addCamera({
              name: nextAvailableCameraName(cameras, {
                kind: "mounted_body",
                bodyName: bodies?.[bodyUuid]?.name || String(bodyUuid),
              }),
              kind: "mounted",
              position: [0, 0, 0.5],
              orientation: [1, 0, 0, 0],
              fovy: 65,
              width: 800,
              height: 800,
              fps: 8,
              mountType: "body",
              mountBodyUuid: bodyUuid,
            })
          : sceneActions.addCamera({
              name: nextAvailableCameraName(cameras, { kind: "world" }),
              kind: "fixed",
              position: [0, 0.5, 2],
              orientation: [1, 0, 0, 0],
              fovy: 65,
              width: 800,
              height: 800,
              fps: 8,
            });
        if (cameraUuid) {
          dispatchViewportCameraVisibility(true);
          focusNewInventoryEntity(() => selection.selectCamera(cameraUuid));
        }
      }
    },
    [
      bodies,
      cameras,
      ensurePreferredBody,
      equalities,
      firstBodyWithoutInertiaUuid,
      focusNewInventoryEntity,
      getQuickAddDisabledReason,
      joints,
      preferredBodyUuid,
      preferredJointUuid,
      scalarJointUuids,
      sceneActions,
      selectedActuatorJointUuid,
      selectedActuatorTendonUuid,
      selectedTargets.joint,
      tendons,
    ]
  );

  return { getQuickAddDisabledReason, handleQuickAdd };
}
